/**
 * Avatar display and upload component.
 * Shows the user's avatar or initials fallback, with click-to-upload.
 */

import { useRef, useState } from 'react';
import { Camera, X } from 'lucide-react';
import { uploadAvatar, deleteAvatar } from '@/api/auth';

interface AvatarUploadProps {
  avatarUrl: string | null;
  fullName: string;
  onAvatarChange: (url: string | null) => void;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export default function AvatarUpload({ avatarUrl, fullName, onAvatarChange }: AvatarUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    setError('');
    setIsUploading(true);
    try {
      const { avatar_url } = await uploadAvatar(file);
      onAvatarChange(avatar_url);
    } catch {
      setError('Failed to upload avatar.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleRemove() {
    setError('');
    try {
      await deleteAvatar();
      onAvatarChange(null);
    } catch {
      setError('Failed to remove avatar.');
    }
  }

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative group">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={fullName}
            className="h-24 w-24 rounded-full object-cover border-2 border-gray-200"
          />
        ) : (
          <div className="flex h-24 w-24 items-center justify-center rounded-full bg-indigo-600 text-2xl font-bold text-white border-2 border-indigo-500">
            {getInitials(fullName)}
          </div>
        )}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 text-white opacity-0 transition-opacity group-hover:opacity-100"
        >
          <Camera className="h-6 w-6" />
        </button>
        {avatarUrl && (
          <button
            type="button"
            onClick={handleRemove}
            className="absolute -right-1 -top-1 rounded-full bg-red-500 p-1 text-white shadow hover:bg-red-600"
            title="Remove avatar"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />
      <p className="text-xs text-gray-500">
        {isUploading ? 'Uploading...' : 'Click to change photo'}
      </p>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
